import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaHeart,
  FaRegHeart,
  FaTimes,
  FaVideo,
  FaVolumeUp,
} from "react-icons/fa";

import { toAbsoluteTechniquePhotoUrl } from "../../../../../services/techniquesPhotoServices";

import {
  beltClasses,
  FALLBACK_IMG,
  hasJapaneseChars,
  headerGradient,
  normalizeUV2,
  speakJapanese,
} from "../utils/techniquesUtils";

function onImgError(event) {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK_IMG;
  }
}

export default function TechniqueDrawer({
  isOpen,
  technique,
  isFavorite,
  canFavorite,
  onClose,
  onToggleFavorite,
}) {
  const [toKanaFn, setToKanaFn] = useState(null);
  const triedImport = useRef(false);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (triedImport.current) return;

    triedImport.current = true;

    (async () => {
      try {
        const mod = await import("wanakana");

        if (mod?.toKana) {
          setToKanaFn(() => mod.toKana);
        }
      } catch {
        // facultatif
      }
    })();
  }, []);

  const {
    image1_url,
    nom,
    traduction,
    famille,
    ceinture,
    video_url,
    uv2,
    description,
    contenu,
  } = technique || {};

  const imgSrc = toAbsoluteTechniquePhotoUrl(image1_url) || FALLBACK_IMG;

  const jpForTts = useMemo(() => {
    const value = String(nom || "").trim();

    if (!value) return "";

    if (hasJapaneseChars(value)) return value;

    const cleaned = value.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();

    return toKanaFn ? toKanaFn(cleaned) : cleaned;
  }, [nom, toKanaFn]);

  const uv2List = useMemo(() => normalizeUV2(uv2), [uv2]);

  const openVideo = () => {
    if (!video_url) return;

    window.open(video_url, "_blank", "noopener,noreferrer");
  };

  if (!isOpen || !technique) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className={`bg-gradient-to-r ${headerGradient(ceinture)} p-4 text-white sm:p-5`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1 ${beltClasses(
                    ceinture
                  )}`}
                >
                  {ceinture || "Ceinture ?"}
                </span>

                {famille && (
                  <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold text-white ring-1 ring-white/20">
                    {famille}
                  </span>
                )}
              </div>

              <h2 className="mt-3 text-2xl font-bold leading-tight">
                {nom || "Technique"}
              </h2>

              {traduction && (
                <p className="mt-1 text-sm italic text-white/90">
                  {traduction}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => speakJapanese(jpForTts)}
              className="inline-flex items-center gap-2 rounded-2xl bg-white/15 px-4 py-2 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/25"
            >
              <FaVolumeUp />
              Prononcer
            </button>

            {video_url && (
              <button
                type="button"
                onClick={openVideo}
                className="inline-flex items-center gap-2 rounded-2xl bg-white/15 px-4 py-2 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/25"
              >
                <FaVideo />
                Vidéo
              </button>
            )}

            <button
              type="button"
              onClick={onToggleFavorite}
              disabled={!canFavorite}
              className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold ring-1 transition disabled:cursor-not-allowed disabled:opacity-50 ${
                isFavorite
                  ? "bg-red-500 text-white ring-red-300 hover:bg-red-600"
                  : "bg-white/15 text-white ring-white/20 hover:bg-white/25"
              }`}
            >
              {isFavorite ? <FaHeart /> : <FaRegHeart />}
              {isFavorite ? "Favori" : "Ajouter favori"}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          <div className="grid gap-5 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <div
                className="overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950"
                style={{ aspectRatio: "551 / 617" }}
              >
                <img
                  src={imgSrc}
                  alt={nom || "Technique"}
                  className="h-full w-full object-contain"
                  onError={onImgError}
                />
              </div>
            </div>

            <div className="space-y-4 lg:col-span-2">
              <InfoCard label="Nom japonais" value={nom || "—"} />
              <InfoCard label="Traduction" value={traduction || "—"} />
              <InfoCard label="Famille" value={famille || "—"} />
              <InfoCard label="Ceinture" value={ceinture || "—"} />

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Fiches UV2
                </p>

                {uv2List.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {uv2List.map((item) => (
                      <span
                        key={item}
                        className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    —
                  </p>
                )}
              </div>
            </div>
          </div>

          {(description || contenu) && (
            <div className="mt-5 rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Description
              </p>

              <div className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-300">
                {description || contenu}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white/95 p-3 dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-2 text-sm font-bold text-slate-900 dark:text-white">
        {value}
      </p>
    </div>
  );
}